import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import ReactDatePicker from "react-date-picker";
import moment from "moment";
import { Button } from "Components/Common/Button/Button";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import {
  NOTE_TYPE,
  NOTE_COMPANIES,
  TOP_SYMPTOMS,
  filterConditions,
  buildNoteDescription,
  applyDiagnosis,
} from "Constants/DoctorNoteTemplates";
import {
  useNoteDayUsage,
  useEligibleNoteVisits,
  useCreateDoctorNote,
  useEditDoctorNote,
} from "CustomHooks/useDoctorNoteMutations";
import { useNotePdfInvalidate } from "CustomHooks/useNotePdfInvalidate";
import { ViewPdf } from "Services/API/VisitPdf.service";
import "Components/Common/DatePicker/DatePicker.css";
import "Colors.css";

const MIN_CONTENT_LENGTH = 10;

const toDateOrNull = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

/** End date + 1 day. The patient never types a return date — it is always derived. */
const deriveReturnDate = (endDate) => {
  const d = toDateOrNull(endDate);
  return d ? moment(d).add(1, "day").toDate() : null;
};

const DateField = ({ label, value, onChange, required }) => (
  <div>
    <label className="block bali_hai text-xs font-medium mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <ReactDatePicker
      className="DatePicker_Input"
      format="MM-dd-y"
      value={value || ""}
      dayPlaceholder="dd"
      monthPlaceholder="mm"
      yearPlaceholder="yyyy"
      clearIcon={null}
      onChange={onChange}
    />
  </div>
);

/**
 * Days used / remaining in the rolling 30-day window.
 *
 * When editing, `usage` already excludes the note being edited, so `thisNoteDays`
 * is drawn separately — that band is what disappears when the note is invalidated,
 * which is why the remaining figure jumps.
 */
const DayBudget = ({ usage, thisNoteDays, isLoading }) => {
  if (isLoading || !usage) {
    return (
      <div className="border border-gray-200 rounded-lg p-3 mb-4 text-xs bali_hai">
        Loading day usage…
      </div>
    );
  }

  const limit = usage.limit || 10;
  const used = usage.daysUsed || 0;
  const mine = Math.max(0, Math.min(thisNoteDays || 0, limit - used));
  const remaining = Math.max(0, limit - used - mine);
  const pct = (n) => `${(n / limit) * 100}%`;

  return (
    <div className="border border-gray-200 rounded-lg p-3 mb-4">
      <div className="flex justify-between items-baseline">
        <span className="text-xs bali_hai">Last {usage.windowDays || 30} days</span>
        <span className="text-sm sapphire">
          <b className={remaining === 0 ? "text-red-500" : "blue_chill"}>{remaining}</b>
          <span className="bali_hai"> of {limit} days remaining</span>
        </span>
      </div>
      <div className="flex h-2 rounded-full bg-gray-200 mt-2 overflow-hidden">
        <span className="h-full bg-blue-chill" style={{ width: pct(used) }} />
        {mine > 0 && <span className="h-full bg-yellow-500" style={{ width: pct(mine) }} />}
      </div>
      <div className="flex gap-4 flex-wrap mt-2 text-xs bali_hai">
        <span>{used} used by other notes</span>
        {mine > 0 && <span>{mine} this note</span>}
        <span>{remaining} remaining</span>
      </div>
    </div>
  );
};

/**
 * Create or edit a doctor note as an admin.
 *
 * Editing never rewrites a note in place — an issued note is a legal document, so the
 * backend invalidates it and issues a replacement. Invalidating first releases the
 * original's days, which is what lets the same dates be re-used without tripping the
 * 30-day limit.
 *
 * Admins may only issue the standard doctor note or its Amazon variant, so there is no
 * note-type picker — just the Amazon toggle.
 */
export const DoctorNoteModal = ({
  open,
  onClose,
  patient,
  visitId,
  note,
  onSuccess,
}) => {
  const isEdit = !!note;
  const patientId = patient?.id;
  const pdfId = note?._id || note?.id;

  const [selectedVisitId, setSelectedVisitId] = useState(visitId || "");
  const [isAmazon, setIsAmazon] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [returnDateTouched, setReturnDateTouched] = useState(false);
  const [diagnosis, setDiagnosis] = useState("");
  const [showConditions, setShowConditions] = useState(false);
  const [content, setContent] = useState("");
  const [contentTouched, setContentTouched] = useState(false);
  const [invalidated, setInvalidated] = useState(false);
  const [confirmInvalidate, setConfirmInvalidate] = useState(false);
  const [formError, setFormError] = useState("");
  // Admin notes are not delivered automatically during the trial period, so the modal
  // stays open after saving to hand over the PDF rather than closing on success.
  const [issued, setIssued] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const blurTimer = useRef(null);

  // The note being edited holds its own days; exclude it so the figure reflects what
  // can actually be issued once it is replaced.
  const { usage, isLoading: usageLoading } = useNoteDayUsage(patientId, isEdit ? pdfId : undefined);
  const { visits, isLoading: visitsLoading } = useEligibleNoteVisits(patientId, open && !isEdit);
  const { createNoteAsync, isLoading: creating } = useCreateDoctorNote(patientId, selectedVisitId);
  const { editNoteAsync, isLoading: editing } = useEditDoctorNote(patientId, visitId);
  const { invalidatePdf, isLoading: invalidating } = useNotePdfInvalidate(visitId, patientId);

  const busy = creating || editing || invalidating;

  // Seed the form when the modal opens.
  useEffect(() => {
    if (!open) return;
    setFormError("");
    setInvalidated(false);
    setConfirmInvalidate(false);
    setContentTouched(false);
    setReturnDateTouched(false);
    setShowConditions(false);
    setDiagnosis("");
    setIssued(null);
    setDownloading(false);

    if (isEdit) {
      setSelectedVisitId(note?.visitId || visitId || "");
      setIsAmazon(String(note?.company || "").toUpperCase() === NOTE_COMPANIES.AMAZON);
      setStartDate(toDateOrNull(note?.startDate));
      setEndDate(toDateOrNull(note?.endDate));
      setReturnDate(toDateOrNull(note?.returnDate));
      setContent(note?.content || "");
    } else {
      setSelectedVisitId(visitId || "");
      setIsAmazon(false);
      setStartDate(null);
      setEndDate(null);
      setReturnDate(null);
      setContent("");
    }
  }, [open, isEdit, note, visitId]);

  // Return date follows end date + 1 until an admin overrides it.
  useEffect(() => {
    if (returnDateTouched) return;
    const derived = deriveReturnDate(endDate);
    if (derived) setReturnDate(derived);
  }, [endDate, returnDateTouched]);

  // Regenerate the body when a template-affecting field changes, unless the admin has
  // hand-edited it (Q6: manual edits win until Amazon or the dates move).
  useEffect(() => {
    if (!open) return;
    if (contentTouched) return;
    if (!startDate || !endDate) return;

    const body = buildNoteDescription(
      {
        patientName: [patient?.firstName, patient?.lastName].filter(Boolean).join(" "),
        dateOfBirth: patient?.dateOfBirth,
        startDate,
        endDate,
        returnDate,
      },
      isAmazon,
    );
    setContent(diagnosis ? applyDiagnosis(body, diagnosis) : body);
  }, [open, contentTouched, isAmazon, startDate, endDate, returnDate, diagnosis, patient]);

  const conditionOptions = useMemo(() => filterConditions(diagnosis).slice(0, 40), [diagnosis]);

  const thisNoteDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const diff = moment(endDate).startOf("day").diff(moment(startDate).startOf("day"), "days") + 1;
    return diff > 0 ? diff : 0;
  }, [startDate, endDate]);

  const alreadySent = !!note?.freshdeskSent;

  const validation = useMemo(() => {
    if (!selectedVisitId) return "Select the visit this note belongs to.";
    if (!startDate || !endDate) return "Start and end dates are required.";
    if (moment(endDate).startOf("day").isBefore(moment(startDate).startOf("day")))
      return "Start Date is ahead of End Date.";
    if (thisNoteDays > 5) return "5 days is the limit for a single note.";
    if (String(content).trim().length < MIN_CONTENT_LENGTH)
      return `Note content must be at least ${MIN_CONTENT_LENGTH} characters.`;
    return "";
  }, [selectedVisitId, startDate, endDate, thisNoteDays, content]);

  const buildPayload = () => ({
    content: String(content).trim(),
    startDate: moment(startDate).startOf("day").toISOString(),
    endDate: moment(endDate).startOf("day").toISOString(),
    returnDate: returnDate ? moment(returnDate).startOf("day").toISOString() : undefined,
    serviceDate: new Date().toISOString(),
    noteType: NOTE_TYPE.DOCTOR_NOTE,
    companies: isAmazon ? [NOTE_COMPANIES.AMAZON] : [],
  });

  const handleSave = async () => {
    if (validation || busy) return;
    setFormError("");
    try {
      // Once the note has been invalidated from this modal there is nothing left to
      // supersede, so this becomes a plain create against the same visit.
      let result;
      if (isEdit && !invalidated) {
        result = await editNoteAsync({ pdfId, ...buildPayload() });
      } else {
        result = await createNoteAsync({ visitId: selectedVisitId, ...buildPayload() });
      }
      // editNote returns { invalidatedPdfId, note }; createNote returns the record.
      const record = result?.note || result;
      setIssued({
        pdfId: record?._id || record?.id,
        replaced: isEdit && !invalidated,
      });
      onSuccess?.(isEdit && !invalidated ? "Note replaced." : "Note created.");
    } catch (err) {
      setFormError(err?.message || "Could not save the note.");
    }
  };

  const handleDownload = async () => {
    if (!issued?.pdfId || downloading) return;
    setDownloading(true);
    setFormError("");
    try {
      const url = await ViewPdf(issued.pdfId);
      if (!url) throw new Error("The PDF is not available yet. Try again in a moment.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setFormError(err?.message || "Could not open the PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const handleInvalidate = () => {
    setConfirmInvalidate(false);
    setFormError("");
    invalidatePdf(
      { pdfId, reason: "Invalidated by admin" },
      {
        // Stays open on purpose: the released days land back in the budget above so a
        // replacement can be written against the updated figure.
        onSuccess: () => setInvalidated(true),
        onError: (err) => setFormError(err?.message || "Could not invalidate the note."),
      },
    );
  };

  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(" ");

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={busy ? () => {} : onClose}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">

              {/* Header — invalidate sits top-right, away from Save */}
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit ? "Edit Doctor Note" : "Create Doctor Note"}
                  </Dialog.Title>
                  <p className="text-xs bali_hai mt-1">
                    {patientName}
                    {patient?.dateOfBirth ? ` · DOB ${moment(patient.dateOfBirth).format("MM-DD-YYYY")}` : ""}
                  </p>
                </div>
                {isEdit && !issued && (
                  invalidated ? (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600 whitespace-nowrap">
                      Invalidated
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setConfirmInvalidate(true)}
                      className="text-xs font-semibold px-3 py-2 rounded-md border border-red-400 text-red-500 hover:bg-red-50 disabled:opacity-50 whitespace-nowrap"
                    >
                      Invalidate Note
                    </button>
                  )
                )}
              </div>

              {/* Note issued. It is NOT sent to the patient — delivery is manual during
                  the trial period, so the modal hands the PDF over instead of closing. */}
              {issued ? (
                <>
                  <div className="border border-green-300 bg-green-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-medium text-green-800">
                      {issued.replaced ? "Note replaced." : "Note created."} It has{" "}
                      <b>not</b> been sent to the patient.
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Download the PDF and email it to {patientName || "the patient"} yourself.
                      Automatic delivery is off while admin-issued notes are being reviewed.
                    </p>
                    <div className="mt-3 flex gap-2 items-center">
                      <Button
                        btnText={downloading ? "Opening…" : "Download PDF"}
                        isDisabled={downloading}
                        onClick={handleDownload}
                        className="text-sm font-medium text-white bg-blue-chill py-2 px-4 inline-flex rounded-md"
                      />
                      {patient?.username && (
                        <a
                          href={`mailto:${patient.username}`}
                          className="text-sm blue_chill underline"
                        >
                          Email {patient.username}
                        </a>
                      )}
                    </div>
                  </div>
                  {formError && <p className="text-xs text-red-500 mb-3">{formError}</p>}
                  <div className="flex justify-end border-t border-gray-100 pt-4">
                    <Button
                      btnText="Done"
                      onClick={onClose}
                      className="text-sm font-medium sapphire bg-transparent border border-gray-200 py-2 px-4 inline-flex rounded-md"
                    />
                  </div>
                </>
              ) : (
              <>
              <DayBudget usage={usage} thisNoteDays={thisNoteDays} isLoading={usageLoading} />

              {confirmInvalidate && (
                <div className="border border-red-300 bg-red-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-red-700">
                    Invalidate this note? It stops verifying at instacured.com/verify and its{" "}
                    {thisNoteDays} day{thisNoteDays === 1 ? "" : "s"} return to the patient's 30-day
                    allowance. This cannot be undone.
                  </p>
                  <p className="text-xs text-red-700 mt-2">
                    {alreadySent
                      ? "This note was already emailed to the patient — that copy cannot be recalled."
                      : "If it is still scheduled for delivery, that delivery is cancelled and the patient will not receive it."}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button btnText="Yes, invalidate" isDisabled={busy} onClick={handleInvalidate}
                      className="text-sm py-2 px-4 rounded-md bg-red-500 text-white" />
                    <Button btnText="Cancel" variant="transparent" isDisabled={busy}
                      onClick={() => setConfirmInvalidate(false)}
                      className="text-sm py-2 px-4 rounded-md border border-gray-200 sapphire bg-transparent" />
                  </div>
                </div>
              )}

              {isEdit && alreadySent && !invalidated && (
                <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-3 mb-4 text-xs text-yellow-800">
                  This note was already emailed to the patient. Invalidating stops it verifying at
                  instacured.com/verify, but the copy they already have cannot be recalled.
                </div>
              )}

              {isEdit && !invalidated && (
                <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-3 mb-4 text-xs text-yellow-800">
                  Saving invalidates this note and issues a replacement. Its {thisNoteDays} day
                  {thisNoteDays === 1 ? "" : "s"} are released first, so re-using the same dates will
                  not count twice. If this note is still scheduled for delivery, that delivery is
                  cancelled. The replacement is not emailed automatically — you download it and send
                  it to the patient.
                </div>
              )}

              <div className="max-h-[55vh] overflow-y-auto pr-1">

                {!isEdit && (
                  <div className="mb-3">
                    <label className="block bali_hai text-xs font-medium mb-1">
                      Visit <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm sapphire"
                      value={selectedVisitId}
                      onChange={(e) => setSelectedVisitId(e.target.value)}
                      disabled={visitsLoading}
                    >
                      <option value="">
                        {visitsLoading ? "Loading visits…" : "Select a visit"}
                      </option>
                      {visits.map((v) => (
                        <option key={v.visitId} value={v.visitId} disabled={v.hasNote}>
                          {moment(v.createdAt).format("MM-DD-YYYY")}
                          {v.isRush ? " · rush" : ""}
                          {v.reason ? ` · ${String(v.reason).slice(0, 40)}` : ""}
                          {v.hasNote ? " · note already issued" : ""}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs bali_hai mt-1">
                      Only visits where the patient paid for a doctor note are listed.
                      {visits.length === 0 && !visitsLoading ? " This patient has none." : ""}
                    </p>
                  </div>
                )}

                <div className="mb-3">
                  <label className="flex items-center gap-2 text-sm sapphire">
                    <input
                      type="checkbox"
                      checked={isAmazon}
                      onChange={(e) => setIsAmazon(e.target.checked)}
                      className="h-4 w-4"
                    />
                    Amazon — use the HR-compliant template
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <DateField label="Start Date" required value={startDate} onChange={setStartDate} />
                  <DateField label="End Date" required value={endDate} onChange={setEndDate} />
                  <DateField
                    label="Return Date"
                    value={returnDate}
                    onChange={(d) => { setReturnDateTouched(true); setReturnDate(d); }}
                  />
                </div>
                <p className="text-xs bali_hai mb-3 -mt-1">
                  Return date follows the end date + 1 day until you change it. Editing these updates
                  the visit's note-reason record too.
                </p>

                <div className="mb-3 relative">
                  <label className="block bali_hai text-xs font-medium mb-1">Diagnosis (ICD-10)</label>
                  <input
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm sapphire"
                    value={diagnosis}
                    placeholder="Search conditions…"
                    onChange={(e) => { setDiagnosis(e.target.value); setShowConditions(true); }}
                    onFocus={() => setShowConditions(true)}
                    // Delay so a click on an option registers before the list closes.
                    onBlur={() => { blurTimer.current = setTimeout(() => setShowConditions(false), 200); }}
                  />
                  {showConditions && conditionOptions.length > 0 && (
                    <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto z-20">
                      {conditionOptions.map((c) => (
                        <div
                          key={c}
                          className="px-3 py-2 text-sm sapphire hover:bg-gray-50 cursor-pointer"
                          onMouseDown={() => {
                            clearTimeout(blurTimer.current);
                            setDiagnosis(c);
                            setShowConditions(false);
                          }}
                        >
                          {c}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {TOP_SYMPTOMS.map((s) => (
                      <button
                        key={s.condition}
                        type="button"
                        onClick={() => setDiagnosis(diagnosis === s.condition ? "" : s.condition)}
                        className={`text-xs px-3 py-1 rounded-full border ${
                          diagnosis === s.condition
                            ? "border-blue-chill blue_chill bg-zircon font-semibold"
                            : "border-gray-200 bali_hai"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-2">
                  <label className="block bali_hai text-xs font-medium mb-1">
                    Note Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm sapphire"
                    rows={8}
                    value={content}
                    onChange={(e) => { setContent(e.target.value); setContentTouched(true); }}
                  />
                  <p className="text-xs bali_hai mt-1">
                    {contentTouched
                      ? "Edited manually — changing Amazon or the dates will rewrite it."
                      : "Generated from the template. Editing it keeps your version."}
                  </p>
                </div>
              </div>

              {(formError || validation) && (
                <p className="text-xs text-red-500 mt-3">{formError || validation}</p>
              )}

              <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-4">
                <Button
                  btnText="Cancel"
                  variant="transparent"
                  isDisabled={busy}
                  onClick={onClose}
                  className="text-sm font-medium sapphire bg-transparent border border-gray-200 py-2 px-4 inline-flex rounded-md"
                />
                <Button
                  btnText={isEdit && !invalidated ? "Save & Reissue" : "Create Note"}
                  isDisabled={!!validation || busy}
                  onClick={handleSave}
                  className="text-sm font-medium text-white bg-blue-chill py-2 px-4 inline-flex rounded-md"
                />
                {busy && <Spinner />}
              </div>
              </>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default DoctorNoteModal;
